import React, { type JSX } from 'react';
import { Box, Typography, Checkbox, List, IconButton, Stack, InputBase } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Search } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { useGetAllLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { CustomTransComponent, GroupPath } from 'components';
import { useNewCustomTranslations } from 'hooks';
import type { SelectedLoad } from './LoadsGroupsDetails/AddLoads';

export const LoadStepTwo = (props: any): JSX.Element => {
    const [searchInput, setSearchInput] = React.useState<string>('');
    const [searchKey, setSearchKey] = React.useState<string>('');
    const hasShownInvalidToastRef = React.useRef<boolean>(false);
    useNewCustomTranslations('WIDGETS');
    useNewCustomTranslations('WIDGET_MANAGEMENT');
    const {
        isLoading,
        isFetching,
        data: loads,
    } = useGetAllLoadsQuery(
        {
            deviceId: props?.createPayload?.sourceDeviceId,
            params: {
                page: 0,
                size: 25,
            },
        },
        { skip: !props?.createPayload?.sourceDeviceId }
    );

    const toggleSelect = (loadId: any): void => {
        const loadIds = JSON.parse(JSON.stringify(props?.createPayload?.loadIds ?? []));

        const index = loadIds.indexOf(loadId);

        if (index !== -1) {
            loadIds.splice(index, 1);
        }
        props?.setCreatePayload?.({
            ...props.createPayload,
            loadIds: loadIds,
        });
    };
    const { t } = useTranslation();
    const allLoads = React.useMemo(() => loads?.data?.loadConfiguration ?? [], [loads?.data?.loadConfiguration]);

    const loadNumberStyle = {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#727E84',
        color: '#fff',
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const getDisplayLoadNumber = (load: SelectedLoad): string => {
        if (load?.loadNumber === undefined || load?.loadNumber === null || load?.loadNumber === '') {
            return '';
        }

        const parsedLoadNumber = Number(load.loadNumber);

        return Number.isNaN(parsedLoadNumber) ? String(load.loadNumber) : String(parsedLoadNumber + 1);
    };
    const handleChannelSelection = (item: any): void => {
        const loadIds = JSON.parse(JSON.stringify(props?.createPayload?.loadIds ?? []));

        const index = loadIds.indexOf(item?.id);

        if (index !== -1) {
            loadIds.splice(index, 1);
        } else {
            loadIds.push(item?.id);
        }
        props?.setCreatePayload?.({
            ...props.createPayload,
            loadIds: loadIds,
        });
    };

    const handleChecked = (item: any): boolean => props?.createPayload?.loadIds?.includes(item?.id);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const nextValue = e.target.value;
        setSearchInput(nextValue);

        const sanitizedValue = nextValue.replace(/[^a-zA-Z0-9@_\-\s]/g, '');
        const hasInvalidCharacters = sanitizedValue !== nextValue;

        setSearchKey(sanitizedValue);

        if (hasInvalidCharacters && !hasShownInvalidToastRef.current) {
            enqueueSnackbar(t('COMMON:PLEASE_PROVIDE_VALID_CHARACTERS'), {
                variant: 'default',
                key: 'invalid-search-chars',
            });
            hasShownInvalidToastRef.current = true;
        }

        if (!hasInvalidCharacters) {
            hasShownInvalidToastRef.current = false;
        }
    };

    const filteredLoads = React.useMemo(() => {
        const normalizedSearch = searchKey.toLowerCase();

        if (normalizedSearch === '') {
            return allLoads;
        }

        return allLoads.filter((item: any) => {
            const loadName = String(item?.loadName ?? item?.name ?? '').toLowerCase();
            const deviceName = String(item?.deviceName ?? '').toLowerCase();
            const groupPath = String(item?.groupPath ?? '').toLowerCase();
            const loadNumber = String(getDisplayLoadNumber(item) ?? '').toLowerCase();

            return (
                loadName.includes(normalizedSearch) ||
                deviceName.includes(normalizedSearch) ||
                groupPath.includes(normalizedSearch) ||
                loadNumber.includes(normalizedSearch)
            );
        });
    }, [allLoads, searchKey]);

    const selectedLoads = React.useMemo(
        () => allLoads.filter((item: any) => props?.createPayload?.loadIds?.includes(item?.id)),
        [allLoads, props?.createPayload?.loadIds]
    );

    return (
        <Box p={2} display="flex" flexDirection="column">
            <Box display="flex" justifyContent={'space-between'}>
                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2', borderRadius: '4px' }}>
                    <Typography variant="h6" gutterBottom fontSize={16} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'WIDGETS:LOADS.SELECT_LOADS_WITHOUT_COUNT'} />
                    </Typography>
                    <Stack my={2} className="border-1 border-radius-4" direction={'row'}>
                        <IconButton type="button" aria-label="search" className="padding-6" sx={{ minWidth: '20%' }}>
                            <Search />
                        </IconButton>
                        <InputBase
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            inputProps={{ 'aria-label': 'search' }}
                            className="w-100"
                            value={searchInput}
                            onChange={handleSearchChange}
                        />
                        {searchInput ? (
                            <IconButton
                                type="button"
                                aria-label="clear search"
                                className="padding-6"
                                onClick={() => {
                                    setSearchInput('');
                                    setSearchKey('');
                                    hasShownInvalidToastRef.current = false;
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        ) : null}
                    </Stack>
                    <Box className="bg-white overflow-y-scroll" sx={{ height: 'calc(100vh - 502px)' }}>
                        <List disablePadding>
                            {isLoading || isFetching ? (
                                <ListSkeleton total={8} />
                            ) : (
                                filteredLoads.map((item: any) => (
                                    <InfoListItem
                                        key={item?.id}
                                        title={''}
                                        subtitle={[
                                            <>
                                                <Stack flexDirection={'row'} alignItems={'center'}>
                                                    <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
                                                    <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                                                        {item?.loadName ?? item?.name}
                                                    </Typography>
                                                </Stack>
                                            </>,
                                        ]}
                                        info={[
                                            <GroupPath
                                                key={item?.id}
                                                pathName={item?.groupPath}
                                                deviceName={item?.deviceName}
                                                width="250px"
                                            />,
                                        ]}
                                        rightComponent={
                                            <Checkbox
                                                onChange={() => handleChannelSelection(item)}
                                                checked={handleChecked(item)}
                                            />
                                        }
                                        hidePadding
                                        divider="full"
                                    />
                                ))
                            )}
                        </List>
                    </Box>
                </Box>

                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2', borderRadius: '4px' }}>
                    <Typography fontSize={16} fontWeight={600} color="#424e54" variant="h6" mb={1}>
                        <CustomTransComponent translationKey={'WIDGET:LOADS.SELECTED_LOADS'} />
                    </Typography>
                    <Typography fontSize={16} fontWeight={400} color="#424e54" variant="h6" mb={2}>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECT_SIXTEEN_LOADS'} />
                    </Typography>
                    <Typography variant="h6" mb={1} fontSize={12} letterSpacing={2} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'COMMON:SELECTED_LOADS'} /> (
                        {props?.createPayload?.loadIds?.length ?? 0}/16)
                    </Typography>
                    <List
                        sx={{ backgroundColor: '#fff', padding: 0, height: 'calc(100vh - 535px)', overflowY: 'scroll' }}
                    >
                        {selectedLoads.map((item: any) => (
                            <InfoListItem
                                key={item?.id}
                                rightComponent={
                                    <IconButton onClick={() => toggleSelect(item?.id)}>
                                        <CloseIcon />
                                    </IconButton>
                                }
                                title=""
                                subtitle={[
                                    <>
                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
                                            <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                                                {item?.loadName ?? item?.name}
                                            </Typography>
                                        </Stack>
                                    </>,
                                ]}
                                info={[
                                    <GroupPath
                                        key={item?.id}
                                        pathName={item?.groupPath}
                                        deviceName={item?.deviceName}
                                        width="250px"
                                    />,
                                ]}
                                sx={{
                                    '& .MuiList-root': {
                                        paddingBottom: 0,
                                    },
                                }}
                                dense
                                divider="full"
                                hidePadding
                            />
                        ))}
                    </List>
                </Box>
            </Box>
        </Box>
    );
};
